import { initializeApp, getApps } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import {
  getDatabase,
  ref,
  onValue,
  get,
  set,
  update,
  remove,
  runTransaction,
} from 'firebase/database';
import { fk } from '../utils/helpers.js';

let db = null;
let sessionCode = null;
let gameRef = null;

/**
 * Initialise Firebase. Safe to call multiple times — idempotent.
 *
 * When `appCheckSiteKey` is present, also wires up Firebase App Check
 * (reCAPTCHA v3) so the Realtime Database can verify requests are coming
 * from the genuine deployed app rather than a copy running elsewhere.
 * In dev (`vite dev`), App Check debug mode is enabled — the SDK prints a
 * debug token to the console on first run; register it once under
 * Firebase Console → App Check → Manage debug tokens to allow local testing.
 *
 * Note: connecting is only half the boot sequence — every game read/write
 * is scoped to a session (`sessions/{code}/game`), so `setSession()` must be
 * called before any of the game functions below.
 *
 * @param {{ apiKey: string, authDomain: string, databaseURL: string, projectId: string, appCheckSiteKey?: string, appId?: string }} config
 *   `appId` (Project Settings → General → Your apps, format "1:NNNNNNNNNNNN:web:XXXXXXXXXXXXXXXX")
 *   is required for App Check — its token-exchange endpoint is keyed by it, and an
 *   absent/undefined appId produces a guaranteed 400 (".../apps/undefined:exchange...").
 */
export function initFirebase(config) {
  const app = getApps().length ? getApps()[0] : initializeApp(config);

  if (config.appCheckSiteKey) {
    if (import.meta.env.DEV) self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(config.appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }

  db = getDatabase(app);
}

/** @returns {boolean} */
export const isInitialised = () => db !== null;

// ── Sessions ────────────────────────────────────────────────────────────────
// Each workshop runs in an isolated session at `sessions/{code}` — its own
// game state and (optionally) its own content pack. Codes are short join
// codes announced in the room (see generateSessionCode in utils/helpers.js).

/**
 * Scope all subsequent game reads/writes to `sessions/{code}/game`.
 * @param {string} code
 */
export function setSession(code) {
  assertReady();
  sessionCode = code;
  gameRef = ref(db, `sessions/${code}/game`);
}

/** @returns {string|null} the active session code */
export const getSessionCode = () => sessionCode;

/** How long a pre-session read/write may take before we assume the database
 *  is unreachable. Without this, a blocked request (offline, or App Check
 *  enforcement rejecting an untokened client) never settles — the SDK retries
 *  silently and the join/create UI hangs forever with no error. */
const SESSION_OP_TIMEOUT_MS = 10000;

function withTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out — database unreachable (network, or App Check rejecting this client)`)), SESSION_OP_TIMEOUT_MS)
    ),
  ]);
}

/** @param {string} code @returns {Promise<boolean>} */
export async function sessionExists(code) {
  assertReady();
  return (await withTimeout(get(ref(db, `sessions/${code}/meta`)), 'Session lookup')).exists();
}

/**
 * Atomically create a session — the transaction guarantees two facilitators
 * generating the same code at the same instant can't both claim it.
 * Also records a lightweight `sessionIndex/{code}` entry (client name +
 * created timestamp) so the facilitator directory can list every session
 * without reading each one's full `game`/`contentPack` tree. The index write
 * is best-effort — a session must still be usable even if the directory
 * entry fails to write (e.g. `sessionIndex` rules not deployed yet); it'll
 * just be missing from the directory until the underlying issue is fixed.
 * @param {string} code
 * @param {string} [clientName]
 * @returns {Promise<boolean>} false if the code is already taken
 */
export async function createSession(code, clientName = '') {
  assertReady();
  let created = false;
  const createdTs = Date.now();
  const result = await withTimeout(
    runTransaction(ref(db, `sessions/${code}/meta`), current => {
      if (current !== null) return; // exists — abort
      created = true;
      return { createdTs, clientName };
    }),
    'Session create'
  );
  if (created && result.committed) {
    try {
      await withTimeout(set(ref(db, `sessionIndex/${code}`), { clientName, createdTs }), 'Session index write');
    } catch (e) {
      console.warn('[firebase] sessionIndex write failed — session created but won\'t appear in the directory:', e);
    }
  }
  return created && result.committed;
}

/**
 * List every session in the facilitator directory, newest first.
 * Reads the lightweight `sessionIndex` node rather than `sessions` itself,
 * so this stays cheap regardless of how much game data each session holds.
 * @returns {Promise<{ code: string, clientName: string, createdTs: number }[]>}
 */
export async function listSessions() {
  assertReady();
  const snap = await withTimeout(get(ref(db, 'sessionIndex')), 'Session list');
  const val = snap.val() ?? {};
  return Object.entries(val)
    .map(([code, meta]) => ({ code, clientName: meta?.clientName ?? '', createdTs: meta?.createdTs ?? 0 }))
    .sort((a, b) => b.createdTs - a.createdTs);
}

/**
 * Permanently delete a session — its game state, content pack, and directory
 * entry. Irreversible; callers must confirm with the facilitator first.
 * @param {string} code
 */
export async function deleteSession(code) {
  assertReady();
  await withTimeout(remove(ref(db, `sessions/${code}`)), 'Session delete');
  try {
    await withTimeout(remove(ref(db, `sessionIndex/${code}`)), 'Session index delete');
  } catch (e) {
    console.warn('[firebase] sessionIndex delete failed — session data removed, but a stale directory entry may remain:', e);
  }
}

/**
 * Subscribe to the active session's game node.
 * @param {(data: object|null) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeToGame(callback) {
  assertSession();
  return onValue(gameRef, snap => callback(snap.val()));
}

/**
 * Subscribe to this client's realtime connection state (`.info/connected`).
 * Fires `true` when the websocket to the database is live, `false` when it
 * drops (e.g. an iPad suspending Safari on screen lock). Used to surface a
 * visible offline indicator so a stale device is obvious in the room.
 * @param {(connected: boolean) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeToConnection(callback) {
  assertReady();
  return onValue(ref(db, '.info/connected'), snap => callback(snap.val() === true));
}

/**
 * Write a value at a path relative to the session's game node.
 * @param {string} path  e.g. 'round'
 * @param {*} value
 */
export async function writeGame(path, value) {
  assertSession();
  await set(ref(db, gamePath(path)), value);
}

/**
 * Merge updates into the session's game node.
 * Keys are relative paths, e.g. { 'subs/team_1_R0': {...} }.
 * @param {Record<string, *>} updates
 */
export async function updateGame(updates) {
  assertSession();
  const prefixed = Object.fromEntries(
    Object.entries(updates).map(([k, v]) => [gamePath(k), v])
  );
  await update(ref(db), prefixed);
}

/**
 * Atomically claims the next free canonical team slot (the first entry in
 * `slotNames` not already present under the session's `teams` node) for a
 * newly-registering team, recording their chosen display name alongside it.
 * Using a transaction on the whole `teams` node means two devices registering
 * at the same instant can't be handed the same slot — the database resolves
 * the conflict and retries one of them against the other's committed result.
 *
 * Returns the claimed canonical name (e.g. 'Team 3'), or null if every slot
 * in `slotNames` is already taken.
 *
 * @param {string[]} slotNames  ordered canonical slot identities, e.g. TEAM_NAMES
 * @param {{ displayName: string, members: string[] }} info
 * @returns {Promise<string|null>}
 */
export async function claimTeamSlot(slotNames, { displayName, members }) {
  assertSession();
  let claimed = null;
  await runTransaction(ref(db, gamePath('teams')), current => {
    const data = current ?? {};
    const slot = slotNames.find(name => !data[fk(name)]);
    if (!slot) { claimed = null; return current; } // every slot taken — abort, no changes
    claimed = slot;
    return { ...data, [fk(slot)]: { name: slot, displayName, members, ts: Date.now() } };
  });
  return claimed;
}

/**
 * Remove a path relative to the session's game node.
 * @param {string} path
 */
export async function removeGame(path) {
  assertSession();
  await remove(ref(db, gamePath(path)));
}

/**
 * Atomic reset — writes a complete fresh state object to the session's game
 * node. Session metadata and content pack are untouched.
 * @param {object} freshState
 */
export async function resetGame(freshState) {
  assertSession();
  await set(gameRef, freshState);
}

// ── Content pack ────────────────────────────────────────────────────────────
// Saved per session (`sessions/{code}/contentPack`) so concurrent client
// workshops can run different content. Reads fall back to the root-level
// `contentPack` node — the deployment-wide default — then to the built-in
// content. Stored outside the game node, so "Reset all" never touches it.

/**
 * One-time read of the effective content pack (null when none saved).
 * Read at boot, not subscribed: swapping simulation content under devices
 * mid-round is never desirable — devices pick up a new pack on reload.
 * @returns {Promise<object|null>}
 */
export async function readContentPack() {
  assertSession();
  const own = await withTimeout(get(ref(db, `sessions/${sessionCode}/contentPack`)), 'Content pack read');
  if (own.exists()) return own.val();
  const root = await withTimeout(get(ref(db, 'contentPack')), 'Content pack read');
  return root.val();
}

/** @param {object} pack */
export async function writeContentPack(pack) {
  assertSession();
  await set(ref(db, `sessions/${sessionCode}/contentPack`), { ...pack, version: 1, ts: Date.now() });
}

/** Remove this session's pack — devices fall back to the deployment default, then built-ins. */
export async function clearContentPack() {
  assertSession();
  await remove(ref(db, `sessions/${sessionCode}/contentPack`));
}

function gamePath(path) {
  return `sessions/${sessionCode}/game/${path}`;
}

function assertReady() {
  if (!db) throw new Error('Firebase not initialised — call initFirebase() first');
}

function assertSession() {
  assertReady();
  if (!sessionCode || !gameRef) throw new Error('No active session — call setSession() first');
}
